import {
  AccountCircleOutlined,
  ApartmentOutlined,
  AssignmentLateOutlined,
  CheckCircleOutlineOutlined,
  Clear,
  CommentOutlined,
  LocationOnOutlined,
  NotificationsNoneOutlined,
  PeopleOutlined,
  PersonOutlined,
  SpeakerNotesOutlined,
  TaskOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import EventIcon from "@mui/icons-material/Event";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, FormGroup, Input, Row } from "reactstrap";

import { H5, MUIIcons, P, SecondaryButton } from "../../AbstractElements";
import "./style.scss";
import { ALL_USERS_STATUS } from "../../Api/constants";
import Divider from "../../CommonElements/Divider";
import LabelTooltip from "../../CommonElements/LabelTooltip";
import CommonModal from "../../CommonElements/Modal";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  getActionMenuData,
  getActionMenuOnboardingData,
} from "../../ReduxToolkit/Reducers/ActionMenuSlice";
import { reviewTrainerOrNominee } from "../../ReduxToolkit/Reducers/CourseSlice";
import {
  getUsersList,
  markActionMenuRead,
} from "../../ReduxToolkit/Reducers/UserSlice";
import {
  AddComment,
  Approve,
  ApproveConfirmation,
  ApproveCourseSchedule,
  AssessmentsAndContentApprovals,
  BookedAttendees,
  CollapseAll,
  Confirm,
  CourseDuration,
  CurrentValue,
  DateOfJoining,
  Department,
  Description,
  Designation,
  EmployeeManagement,
  EmptyCommentsAlert,
  Enter,
  ExpandAll,
  FeedbackRequests,
  Location,
  NoItemsFound,
  NominationAndExtensionRequests,
  NominationEndsOn,
  PleaseSelectReason,
  PreviousValue,
  Reason,
  Reject,
  RejectCourseSchedule,
  SearchAllItems,
  Select,
  SideBarTitleMyActionMenu,
  SideBarTitleNominationRequest,
  SkillAssessmentAndApprovals,
  Source,
  SubmittedOn,
  TrainerAndTrainingActions,
  TrainingNotifications,
  EmployeeType,
  UsersGoingToBeSuspended,
  ValidTill,
  TrainingImpressionFor30Days,
  CourseName,
  TrainingImpressionFor60Days,
  TrainingImpressionFor90Days,
  MarkAsRead,
} from "../../utils/Constant";
import {
  declineNomineeRMReasons,
  employeeTypeMap,
  formatCustomDate,
  formatDateTime,
  formatLogValue,
  isTodayWithinDateRange,
  logLabelKeys,
  logLabels,
  showToast,
} from "../../utils/helper/helper";
import { hasPermissionToComponent } from "../../utils/helper/permission";

const ActionMenu = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [comments, setComments] = useState("");

  const [selectedId, setSelectedId] = useState("");
  const { usersList } = useAppSelector(state => state.user);
  const dispatch = useAppDispatch();
  const [userNameMap, setUserNameMap] = useState(null);
  const { actionMenu, actionMenuOnboarding } = useAppSelector(
    state => state.actionMenu
  );
  const [expandedCategories, setExpandedCategories] = useState({
    employee_onboarding: true,
    valid_till_users: true,
    nomination_request: true,
    pending_skill_submissions: true,
    assessment_review: true,
    course_session_assignment: true,
    feedback: true,
    training_schedules: true,
    training_feedback_90_days: true,
    training_feedback_60_days: true,
    training_feedback_30_days: true,
  });
  const [selectedModalType, setSelectedModalType] = useState("");
  const [accordionData, setAccordionData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    getMenuData();
    if (hasPermissionToComponent("USER_MANAGEMENT.ROOT")) {
      dispatch(getUsersList({ role: ALL_USERS_STATUS }));
    }
  }, []);

  const [removingItems, setRemovingItems] = useState(new Set());
  const [loadingStates, setLoadingStates] = useState({});

  const acknowledgeActionItem = async actionItemId => {
    setLoadingStates(prev => ({ ...prev, [actionItemId]: true }));
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setRemovingItems(prev => new Set([...prev, actionItemId]));
      setTimeout(() => {
        const payload = {
          audit_log_id: actionItemId,
          type: "action-menu",
        };
        dispatch(markActionMenuRead({ payload })).then(() => {
          getMenuData();
        });
      }, 500);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [actionItemId]: false }));
    }
  };

  const getMenuData = async () => {
    setAccordionData({});
    await dispatch(getActionMenuData());
    await dispatch(getActionMenuOnboardingData());
  };

  useEffect(() => {
    const userMapObj: Record<number, { user_name: string }> = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user;
    });
    setUserNameMap(userMapObj);
  }, [usersList]);

  const isEmpty = val =>
    val &&
    (Array.isArray(val)
      ? val.length === 0
      : typeof val === "object" && Object.keys(val).length === 0);

  useEffect(() => {
    const data = {};
    data["employee_onboarding"] = [];
    data["valid_till_users"] = [];
    data["nomination_request"] = [];
    data["pending_skill_submissions"] = [];
    data["assessment_review"] = [];
    data["course_session_assignment"] = [];
    data["training_schedules"] = [];
    data["feedback"] = [];
    data["training_feedback_30_days"] = [];
    data["training_feedback_60_days"] = [];
    data["training_feedback_90_days"] = [];

    if (actionMenu && actionMenu?.data) {
      const searchLower = searchTerm.toLowerCase();
      if (!isEmpty(actionMenu?.data?.standalone_assessment_review)) {
        data["assessment_review"].push(
          ...actionMenu.data.standalone_assessment_review
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.submitted_on
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "standalone_assessment_review",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.nomination_request)) {
        data["nomination_request"].push(
          ...actionMenu.data.nomination_request
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.nomination_end_date
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "nomination_request",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.training_feedback_30_days)) {
        data["training_feedback_30_days"].push(
          ...actionMenu.data.training_feedback_30_days
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "training_feedback_30_days",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.training_feedback_60_days)) {
        data["training_feedback_60_days"].push(
          ...actionMenu.data.training_feedback_60_days
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "training_feedback_60_days",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.training_feedback_90_days)) {
        data["training_feedback_90_days"].push(
          ...actionMenu.data.training_feedback_90_days
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "training_feedback_90_days",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.review_pending_skill_submissions)) {
        data["pending_skill_submissions"].push(
          ...actionMenu.data.review_pending_skill_submissions
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.submitted_on
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "review_pending_skill_submissions",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.pending_skill_assignments)) {
        data["pending_skill_submissions"].push(
          ...actionMenu.data.pending_skill_assignments
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "pending_skill_assignments",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.self_assessment_review)) {
        data["assessment_review"].push(
          ...actionMenu.data.self_assessment_review
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.submitted_on
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "self_assessment_review",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.course_feedback)) {
        data["feedback"].push(
          ...actionMenu.data.course_feedback
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "course_feedback",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.module_feedback)) {
        data["feedback"].push(
          ...actionMenu.data.module_feedback
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "module_feedback",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.course_session_assignment)) {
        data["course_session_assignment"].push(
          ...actionMenu.data.course_session_assignment
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.booked_count
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "course_session_assignment",
            }))
        );
      }

      if (!isEmpty(actionMenu?.data?.training_schedules)) {
        data["training_schedules"].push(
          ...actionMenu.data.training_schedules
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.start_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                formatCustomDate(item?.end_date)
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.location?.toString()?.toLowerCase()?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "training_schedules",
            }))
        );
      }
    }

    if (actionMenuOnboarding && actionMenuOnboarding?.data) {
      const searchLower = searchTerm.toLowerCase();
      if (!isEmpty(actionMenuOnboarding?.data?.onboarding)) {
        data["employee_onboarding"].push(
          ...actionMenuOnboarding.data.onboarding
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.date_of_joining
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.department
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.designation
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "onboarding",
            }))
        );
      }

      if (!isEmpty(actionMenuOnboarding?.data?.employee_data_change)) {
        data["employee_onboarding"].push(
          ...actionMenuOnboarding.data.employee_data_change
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.audit_logs?.some(log =>
                  Object.keys(logLabelKeys).some(
                    key =>
                      log?.logs?.[logLabelKeys[key]]?.old_value
                        ?.toString()
                        ?.toLowerCase()
                        ?.includes(searchLower) ||
                      log?.logs?.[logLabelKeys[key]]?.new_value
                        ?.toString()
                        ?.toLowerCase()
                        ?.includes(searchLower)
                  )
                )
            )
            .map(item => {
              const cleanedAuditLogs =
                item.audit_logs
                  ?.map(log => {
                    const filteredLogs = Object.fromEntries(
                      Object.entries(log.logs || {}).filter(
                        ([field]) => logLabelKeys[field]
                      )
                    );
                    return {
                      ...log,
                      logs: filteredLogs,
                    };
                  })
                  .filter(log => !isEmpty(log.logs)) || [];
              if (cleanedAuditLogs.length === 0) {
                return null;
              }
              return {
                ...item,
                audit_logs: cleanedAuditLogs,
                data_key: "employee_data_change",
              };
            })
            .filter(Boolean)
        );
      }

      if (!isEmpty(actionMenuOnboarding?.data?.valid_till_users)) {
        data["valid_till_users"].push(
          ...actionMenuOnboarding.data.valid_till_users
            .filter(
              item =>
                item?.message
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.valid_till
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower) ||
                item?.employee_type
                  ?.toString()
                  ?.toLowerCase()
                  ?.includes(searchLower)
            )
            .map(item => ({
              ...item,
              data_key: "valid_till_users",
            }))
        );
      }
    }

    setAccordionData(data);
  }, [actionMenu, actionMenuOnboarding, searchTerm]);

  const toggleCategory = category => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleAllCategories = expanded => {
    setExpandedCategories({
      employee_onboarding: expanded,
      valid_till_users: expanded,
      nomination_request: expanded,
      pending_skill_submissions: expanded,
      assessment_review: expanded,
      course_session_assignment: expanded,
      feedback: expanded,
      training_schedules: expanded,
      training_feedback_30_days: expanded,
      training_feedback_60_days: expanded,
      training_feedback_90_days: expanded,
    });
  };

  const handleAction = (categoryKey, itemId, action) => {
    if (categoryKey === "employee_onboarding" && action === "navigate") {
      const baseUrl = `/user-management/user-details?user_id=${itemId?.user_id}&job_role_id=${itemId?.job_role_id}`;
      const newTab = window.open(baseUrl, "_blank");

      if (newTab) {
        newTab.focus();
      }
    }
    if (categoryKey === "valid_till_users" && action === "internal") {
      const baseUrl = `/user-management/user-details?user_id=${itemId?.user_id}&job_role_id=${itemId?.job_role_id}`;
      const newTab = window.open(baseUrl, "_blank");

      if (newTab) {
        newTab.focus();
      }
    }

    if (categoryKey === "valid_till_users" && action === "external") {
      navigate("/add-employee", {
        state: { userId: itemId },
      });
    }

    if (categoryKey === "nomination_request") {
      setSelectedModalType(action);
      setSelectedId(itemId);
      toggleModal();
    }

    if (categoryKey === "training_feedback_30_days") {
      return navigate(
        `/training-impressions?training_feedback_id=${itemId.training_feedback_id}`
      );
    }
    if (categoryKey === "training_feedback_60_days") {
      return navigate(
        `/training-impressions?training_feedback_id=${itemId.training_feedback_id}`
      );
    }
    if (categoryKey === "training_feedback_90_days") {
      return navigate(
        `/training-impressions?training_feedback_id=${itemId.training_feedback_id}`
      );
    }

    if (categoryKey === "review_pending_skill_submissions") {
      const baseUrl = `/user-management/user-details?user_id=${itemId}&tab=pending_skill_update`;
      const newTab = window.open(baseUrl, "_blank");

      if (newTab) {
        newTab.focus();
      }
    }

    if (categoryKey === "pending_skill_assignments") {
      navigate("/my-learning-plan?tab=pending_skill_update");
    }

    if (categoryKey === "self_assessment_review") {
      navigate(
        `/assessments/review-self-assessments?participant_id=${itemId}&review=true`
      );
    }

    if (categoryKey === "standalone_assessment_review") {
      navigate("/assessments/review-standalone-assessment", {
        state: { participantId: itemId },
      });
    }

    if (categoryKey === "course_session_assignment") {
      setSelectedModalType(action);
      setSelectedId(itemId);
      toggleModal();
    }

    if (categoryKey === "course_feedback") {
      navigate("/schedule-list/course-details/course-feedback", {
        state: {
          course_name: itemId?.course_name,
          location_name: itemId?.location_name,
          classroom_name: itemId?.classroom_name,
          tranierName: itemId?.tranierName,
          course_start_date: itemId?.course_start_date,
          course_end_date: itemId?.course_end_date,
          course_schedule_id: itemId?.course_schedule_id,
        },
      });
    }

    if (categoryKey === "module_feedback") {
      navigate("/schedule-list/course-details/module-feedback", {
        state: {
          course_name: itemId?.course_name,
          location_name: itemId?.location_name,
          classroom_name: itemId?.classroom_name,
          tranierName: itemId?.tranierName,
          course_start_date: itemId?.course_start_date,
          course_end_date: itemId?.course_end_date,
          course_schedule_id: itemId?.course_schedule_id,
        },
      });
    }

    if (categoryKey === "training_schedules") {
      if (isTodayWithinDateRange(itemId.start_date, itemId.end_date)) {
        return navigate(
          `/schedule-list/course-details?course_id=${itemId.course_id}&schedule_id=${itemId.schedule_id}`
        );
      }
      navigate(`/course-catalog/course-details?course_id=${itemId.course_id}`);
    }
  };

  const transformLogs = logs => {
    return logs?.map(logEntry => {
      const transformedData = [];
      Object.entries(logEntry.logs).forEach(([key, value]) => {
        const sourceLabel = logLabels[key];
        if (!sourceLabel) return;

        const previousValue = formatLogValue(value["old_value"]);
        const currentValue = formatLogValue(value["new_value"]);
        if (sourceLabel) {
          transformedData.push({
            sourceLabel,
            previousValue,
            currentValue,
          });
        }
      });

      return {
        date: formatDateTime(logEntry.created_at),
        name: userNameMap ? userNameMap[logEntry.created_by]?.user_name : null,
        data: transformedData,
      };
    });
  };

  const toggleModal = () => {
    if (isModalOpen) {
      setSelectedModalType("");
      setSelectedReason("");
      setComments("");
      setSelectedId("");
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  };

  const getModalTitle = () => {
    if (
      selectedModalType === "nomination_reject" ||
      selectedModalType === "nomination_approve"
    )
      return SideBarTitleNominationRequest;
    if (selectedModalType === "schedule_approve") {
      return Approve;
    } else if (selectedModalType === "schedule_reject") {
      return Reject;
    }
  };

  const handlePrimaryBtnClick = async () => {
    if (selectedModalType === "nomination_reject" && selectedReason === "") {
      showToast(PleaseSelectReason, "error");
      return;
    }
    if (
      selectedModalType === "nomination_reject" &&
      selectedReason === "others_please_specify"
    ) {
      if (!comments || comments?.trim() === "") {
        showToast(EmptyCommentsAlert, "error");
        return;
      }
    }

    let payload;
    if (selectedModalType === "nomination_approve") {
      payload = {
        action: "approved",
        type: "nominee",
        id: selectedId?.toString(),
        other_reason: comments,
      };
      await dispatch(reviewTrainerOrNominee({ review: payload }));
    } else if (selectedModalType === "nomination_reject") {
      payload = {
        action: "rejected",
        type: "nominee",
        id: selectedId?.toString(),
        reason: selectedReason,
        other_reason:
          selectedReason === "others_please_specify" ? comments : "",
      };
      await dispatch(reviewTrainerOrNominee({ review: payload }));
    }

    if (selectedModalType === "schedule_approve") {
      payload = {
        action: "approved",
        type: "trainer",
        id: null,
        reason: "",
        course_schedule_id: selectedId,
      };
      await dispatch(reviewTrainerOrNominee({ review: payload }));
    } else if (selectedModalType === "schedule_reject") {
      payload = {
        action: "rejected",
        type: "trainer",
        id: null,
        reason: "",
        course_schedule_id: selectedId,
      };
      await dispatch(reviewTrainerOrNominee({ review: payload }));
    }

    getMenuData();
    toggleModal();
  };

  const renderModalBody = () => {
    return (
      <>
        {selectedModalType === "nomination_reject" && (
          <>
            <LabelTooltip
              label={Reason}
              tooltipText={`${Select} ${Reason}`}
              important
            />
            <Input
              type="select"
              id="reason-dropdown"
              className="mb-2"
              value={selectedReason}
              onChange={event => {
                setSelectedReason(event.target.value);
              }}
            >
              <option value="" disabled>
                {`${Select} ${Reason}`}
              </option>
              {declineNomineeRMReasons?.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </Input>
          </>
        )}
        {selectedModalType === "nomination_reject" &&
          selectedReason === "others_please_specify" && (
            <FormGroup>
              <LabelTooltip
                label={AddComment}
                tooltipText={`${Enter} ${Description}`}
                important
              />
              <Input
                onChange={event => {
                  if (
                    (event.target.value && event.target.value.length <= 255) ||
                    event.target.value === ""
                  ) {
                    setComments(event.target.value);
                  }
                }}
                rows={4}
                name="objective"
                type="textarea"
                placeholder={`${Enter} ${Description}`}
                maxLength={255}
              />
            </FormGroup>
          )}
        {selectedModalType === "nomination_approve" && (
          <P>{ApproveConfirmation}</P>
        )}
        {selectedModalType === "schedule_approve" && (
          <P>{ApproveCourseSchedule}</P>
        )}
        {selectedModalType === "schedule_reject" && (
          <P>{RejectCourseSchedule}</P>
        )}
      </>
    );
  };

  return (
    <div className="page-body">
      <CommonModal
        sizeTitle={getModalTitle()}
        modalBodyClassName=""
        isOpen={isModalOpen}
        toggle={toggleModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={handlePrimaryBtnClick}
        primaryBtnText={Confirm}
      >
        {renderModalBody()}
      </CommonModal>
      <Card>
        <CardBody>
          <div className="action-menu-header">
            <h2 className="action-menu-header__title">
              {SideBarTitleMyActionMenu}
            </h2>
            <div className="action-menu-header__controls">
              <div className="action-menu-header__search-container">
                <div className="action-menu-header__search-icon mt-1">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </div>
                <input
                  type="text"
                  placeholder={SearchAllItems}
                  className="action-menu-header__search-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Clear
                    sx={{
                      fontSize: 18,
                      cursor: "pointer",
                      position: "absolute",
                      right: "10px",
                      color: "#aaa",
                    }}
                    onClick={() => setSearchTerm("")}
                    className="mt-2"
                  />
                )}
              </div>
              <SecondaryButton onClick={() => toggleAllCategories(true)}>
                {ExpandAll}
              </SecondaryButton>
              <SecondaryButton onClick={() => toggleAllCategories(false)}>
                {CollapseAll}
              </SecondaryButton>
            </div>
          </div>

          <div className="action-menu-categories">
            {Object.keys(accordionData)?.map(item => {
              if (
                item === "employee_onboarding" &&
                hasPermissionToComponent("ACTION_MENU.EMPLOYEE_ENGAGEMENT")
              ) {
                return (
                  <CategorySection
                    key="0"
                    title={EmployeeManagement}
                    icon={<PersonOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.employee_onboarding}
                    toggleExpanded={() => toggleCategory("employee_onboarding")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <div className="item-card__container">
                                <p className="item-card__content-title">
                                  {item?.message}
                                </p>
                                <span className="action-section-wrapper">
                                  <div className="action-section">
                                    <button
                                      onClick={() =>
                                        acknowledgeActionItem(item.audit_log_id)
                                      }
                                      disabled={
                                        loadingStates[item.audit_log_id] ||
                                        removingItems.has(item.audit_log_id)
                                      }
                                      className="action-button"
                                      title={MarkAsRead}
                                    >
                                      {loadingStates[item.audit_log_id] ? (
                                        <div className="button-state loading">
                                          <div className="spinner"></div>
                                        </div>
                                      ) : removingItems.has(
                                          item.audit_log_id
                                        ) ? (
                                        <div className="button-state success">
                                          <CheckCircleOutlineOutlined className="button-icon" />
                                        </div>
                                      ) : (
                                        <div className="button-state default">
                                          <CheckCircleOutlineOutlined className="button-icon" />
                                        </div>
                                      )}
                                    </button>
                                    <span>{`(${MarkAsRead})`}</span>
                                  </div>
                                </span>
                              </div>

                              {item?.data_key === "onboarding" && (
                                <>
                                  <div className="item-card__content-info">
                                    <EventIcon sx={{ fontSize: 16 }} />
                                    <span>
                                      {DateOfJoining}:
                                      {formatDate(item.date_of_joining)}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <ApartmentOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Department}: {item.department}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <SpeakerNotesOutlined
                                      sx={{ fontSize: 16 }}
                                    />
                                    <span>
                                      {Designation}: {item.designation}
                                    </span>
                                  </div>
                                </>
                              )}
                              {item?.data_key === "employee_data_change" &&
                                transformLogs(item?.audit_logs)?.map(
                                  (log, index) => {
                                    return (
                                      <div
                                        className="activity-card"
                                        key={index}
                                      >
                                        {(log?.date || log?.name) && (
                                          <div className="d-flex gap-3 align-items-center">
                                            <H5>{log?.date}</H5>
                                            <span>|</span>
                                            <H5>{log?.name}</H5>
                                          </div>
                                        )}
                                        {log?.data?.map((logData, index) => (
                                          <>
                                            <Row key={index} className="mt-3">
                                              <Col
                                                lg="4"
                                                md="4"
                                                sm="12"
                                                xs="12"
                                                className="item-card__content-info"
                                              >
                                                <span className="d-flex gap-1">
                                                  <P>{Source}:</P>
                                                  <P className="fw-bold">
                                                    {logData?.sourceLabel}
                                                  </P>
                                                </span>
                                              </Col>

                                              <Col
                                                lg="4"
                                                md="4"
                                                sm="12"
                                                xs="12"
                                                className="item-card__content-info"
                                              >
                                                <span className="d-flex gap-1">
                                                  <P>{PreviousValue}:</P>
                                                  <P className="fw-bold">
                                                    {logData?.previousValue}
                                                  </P>
                                                </span>
                                              </Col>

                                              <Col
                                                lg="4"
                                                md="4"
                                                sm="12"
                                                xs="12"
                                                className="item-card__content-info"
                                              >
                                                <span className="d-flex gap-1">
                                                  <P>{CurrentValue}:</P>
                                                  <P className="fw-bold">
                                                    {logData?.currentValue}
                                                  </P>
                                                </span>
                                              </Col>
                                            </Row>
                                            {log?.data?.length > 1 && (
                                              <Divider />
                                            )}
                                          </>
                                        ))}
                                      </div>
                                    );
                                  }
                                )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "employee_onboarding",
                                    {
                                      user_id: item?.user_id,
                                      job_role_id: item?.job_role_id,
                                    },
                                    "navigate"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="ArrowForwardOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "valid_till_users" &&
                hasPermissionToComponent("ACTION_MENU.EMPLOYEE_ENGAGEMENT")
              ) {
                return (
                  <CategorySection
                    key="1"
                    title={UsersGoingToBeSuspended}
                    icon={<PersonOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.valid_till_users}
                    toggleExpanded={() => toggleCategory("valid_till_users")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key === "valid_till_users" && (
                                <>
                                  <div className="item-card__content-info">
                                    <EventIcon sx={{ fontSize: 16 }} />
                                    <span>
                                      {ValidTill}:{formatDate(item.valid_till)}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <PersonOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {EmployeeType}:{" "}
                                      {employeeTypeMap[item?.employee_type]}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              {item?.employee_type === "internal" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "valid_till_users",
                                      {
                                        user_id: item?.user_id,
                                        job_role_id: item?.job_role_id,
                                      },
                                      "internal"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                              {item?.employee_type !== "internal" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "valid_till_users",
                                      item?.user_id,
                                      "external"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "nomination_request" &&
                hasPermissionToComponent("ACTION_MENU.NOMINATION_REQUEST")
              ) {
                return (
                  <CategorySection
                    key="2"
                    title={NominationAndExtensionRequests}
                    icon={<AssignmentLateOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.nomination_request}
                    toggleExpanded={() => toggleCategory("nomination_request")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key === "nomination_request" && (
                                <>
                                  <div className="item-card__content-info">
                                    <EventIcon sx={{ fontSize: 16 }} />
                                    <span>
                                      {NominationEndsOn}:
                                      {formatDate(item.nomination_end_date)}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <LocationOnOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Location}: {item.location}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "nomination_request",
                                    item.nomination_id,
                                    "nomination_approve"
                                  )
                                }
                                className="text-success pointer"
                                iconName="CheckCircleOutlined"
                                size={27}
                              />
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "nomination_request",
                                    item.nomination_id,
                                    "nomination_reject"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="CancelOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "pending_skill_submissions" &&
                hasPermissionToComponent(
                  "ACTION_MENU.PENDING_SKILL_SUBMISSIONS"
                )
              ) {
                return (
                  <CategorySection
                    key="3"
                    title={SkillAssessmentAndApprovals}
                    icon={<TaskOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.pending_skill_submissions}
                    toggleExpanded={() =>
                      toggleCategory("pending_skill_submissions")
                    }
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key ===
                                "review_pending_skill_submissions" && (
                                <>
                                  <div className="item-card__content-info">
                                    <EventIcon sx={{ fontSize: 16 }} />
                                    <span>
                                      {SubmittedOn}:
                                      {formatDate(item.submitted_on)}
                                    </span>
                                  </div>
                                </>
                              )}
                              {item?.data_key ===
                                "pending_skill_assignments" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <LocationOnOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Location}: {item.location}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              {item?.data_key ===
                                "review_pending_skill_submissions" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "review_pending_skill_submissions",
                                      item.user_id,
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                              {item?.data_key ===
                                "pending_skill_assignments" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "pending_skill_assignments",
                                      item.user_id,
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "assessment_review" &&
                hasPermissionToComponent("ACTION_MENU.ASSESSMENT_REVIEW")
              ) {
                return (
                  <CategorySection
                    key="4"
                    title={AssessmentsAndContentApprovals}
                    icon={<TaskOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.assessment_review}
                    toggleExpanded={() => toggleCategory("assessment_review")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {(item?.data_key === "self_assessment_review" ||
                                item?.data_key ===
                                  "standalone_assessment_review") && (
                                <>
                                  <div className="item-card__content-info">
                                    <EventIcon sx={{ fontSize: 16 }} />
                                    <span>
                                      {SubmittedOn}:
                                      {formatDate(item.submitted_on)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              {item?.data_key === "self_assessment_review" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "self_assessment_review",
                                      item?.participant_id,
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                              {item?.data_key ===
                                "standalone_assessment_review" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "standalone_assessment_review",
                                      item.participant_id,
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "course_session_assignment" &&
                hasPermissionToComponent(
                  "ACTION_MENU.COURSE_SESSION_ASSIGNMENT"
                )
              ) {
                return (
                  <CategorySection
                    key="5"
                    title={TrainerAndTrainingActions}
                    icon={<AccountCircleOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.course_session_assignment}
                    toggleExpanded={() =>
                      toggleCategory("course_session_assignment")
                    }
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key ===
                                "course_session_assignment" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <LocationOnOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Location}: {item.location}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <PeopleOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {BookedAttendees}: {item.booked_count}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "course_session_assignment",
                                    item.course_schedule_id,
                                    "schedule_approve"
                                  )
                                }
                                className="text-success pointer"
                                iconName="CheckCircleOutlined"
                                size={27}
                              />
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "course_session_assignment",
                                    item.course_schedule_id,
                                    "schedule_reject"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="CancelOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "feedback" &&
                hasPermissionToComponent("ACTION_MENU.FEEDBACK")
              ) {
                return (
                  <CategorySection
                    key="6"
                    title={FeedbackRequests}
                    icon={<CommentOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.feedback}
                    toggleExpanded={() => toggleCategory("feedback")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {(item?.data_key === "course_feedback" ||
                                item?.data_key === "module_feedback") && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <LocationOnOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Location}: {item.location}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              {item?.data_key === "course_feedback" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "course_feedback",
                                      {
                                        course_name: item?.course_name || "-",
                                        location_name:
                                          item?.location_name || "-",
                                        classroom_name:
                                          item?.classroom_name || "-",
                                        tranierName: item?.tranierName || "-",
                                        course_start_date:
                                          item?.course_start_date || "-",
                                        course_end_date:
                                          item?.course_end_date || "-",
                                        course_schedule_id:
                                          item?.course_schedule_id,
                                        course_feedback_id:
                                          item?.course_feedback_id || null,
                                      },
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                              {item?.data_key === "module_feedback" && (
                                <MUIIcons
                                  onClick={() =>
                                    handleAction(
                                      "module_feedback",
                                      {
                                        course_name: item?.course_name || "-",
                                        location_name:
                                          item?.location_name || "-",
                                        classroom_name:
                                          item?.classroom_name || "-",
                                        tranierName: item?.tranierName || "-",
                                        course_start_date:
                                          item?.course_start_date || "-",
                                        course_end_date:
                                          item?.course_end_date || "-",
                                        course_schedule_id:
                                          item?.course_schedule_id,
                                        course_feedback_id:
                                          item?.course_feedback_id || null,
                                      },
                                      "navigate"
                                    )
                                  }
                                  className="primary-icon-color pointer"
                                  iconName="ArrowForwardOutlined"
                                  size={27}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "training_schedules" &&
                hasPermissionToComponent("ACTION_MENU.TRAINING_SCHEDULES")
              ) {
                return (
                  <CategorySection
                    key="7"
                    title={TrainingNotifications}
                    icon={<NotificationsNoneOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.training_schedules}
                    toggleExpanded={() => toggleCategory("training_schedules")}
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key === "training_schedules" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <LocationOnOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {Location}: {item.location}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "training_schedules",
                                    item,
                                    "navigate"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="ArrowForwardOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "training_feedback_30_days" &&
                hasPermissionToComponent("ACTION_MENU.TRAINING_IMPRESSIONS")
              ) {
                return (
                  <CategorySection
                    key="8"
                    title={TrainingImpressionFor30Days}
                    icon={<AssignmentLateOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.training_feedback_30_days}
                    toggleExpanded={() =>
                      toggleCategory("training_feedback_30_days")
                    }
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key ===
                                "training_feedback_30_days" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <AssignmentLateOutlined
                                      sx={{ fontSize: 16 }}
                                    />
                                    <span>
                                      {CourseName}: {item.course_name}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "training_feedback_30_days",
                                    item,
                                    "navigate"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="ArrowForwardOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "training_feedback_60_days" &&
                hasPermissionToComponent("ACTION_MENU.TRAINING_IMPRESSIONS")
              ) {
                return (
                  <CategorySection
                    key="9"
                    title={TrainingImpressionFor60Days}
                    icon={<AssignmentLateOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.training_feedback_60_days}
                    toggleExpanded={() =>
                      toggleCategory("training_feedback_60_days")
                    }
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key ===
                                "training_feedback_60_days" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <AssignmentLateOutlined
                                      sx={{ fontSize: 16 }}
                                    />
                                    <span>
                                      {CourseName}: {item.course_name}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "training_feedback_60_days",
                                    item,
                                    "navigate"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="ArrowForwardOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
              if (
                item === "training_feedback_90_days" &&
                hasPermissionToComponent("ACTION_MENU.TRAINING_IMPRESSIONS")
              ) {
                return (
                  <CategorySection
                    key="10"
                    title={TrainingImpressionFor90Days}
                    icon={<AssignmentLateOutlined sx={{ fontSize: 20 }} />}
                    items={accordionData[item]}
                    expanded={expandedCategories.training_feedback_90_days}
                    toggleExpanded={() =>
                      toggleCategory("training_feedback_90_days")
                    }
                    renderItem={item => {
                      return (
                        <div key={item.id} className="item-card">
                          <div className="item-card__container">
                            <div className="item-card__content w-100">
                              <p className="item-card__content-title">
                                {item?.message}
                              </p>
                              {item?.data_key ===
                                "training_feedback_90_days" && (
                                <>
                                  <div className="item-card__content-info">
                                    <TimerOutlined sx={{ fontSize: 16 }} />
                                    <span>
                                      {CourseDuration}:{" "}
                                      {`${formatCustomDate(item?.start_date)} - ${formatCustomDate(item?.end_date)}`}
                                    </span>
                                  </div>
                                  <div className="item-card__content-info">
                                    <AssignmentLateOutlined
                                      sx={{ fontSize: 16 }}
                                    />
                                    <span>
                                      {CourseName}: {item.course_name}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="item-card__actions">
                              <MUIIcons
                                onClick={() =>
                                  handleAction(
                                    "training_feedback_90_days",
                                    item,
                                    "navigate"
                                  )
                                }
                                className="primary-icon-color pointer"
                                iconName="ArrowForwardOutlined"
                                size={27}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                );
              }
            })}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

const CategorySection = ({
  key,
  title,
  icon,
  items,
  expanded,
  toggleExpanded,
  renderItem,
}) => {
  return (
    <div key={key} className="category-section category-section__red-border">
      <div className="category-section__header" onClick={toggleExpanded}>
        <div className="category-section__title-container">
          {React.cloneElement(icon, { style: { color: "#ee1b24" } })}
          <h3 className="category-section__title">{title}</h3>
          <span className="category-section__count">{items.length}</span>
        </div>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: 20 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20 }} />
        )}
      </div>
      {expanded && (
        <div className="category-section__content">
          {items.length > 0 ? (
            items.map(renderItem)
          ) : (
            <div className="category-section__content--empty">
              {NoItemsFound}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CategorySection.propTypes = {
  key: PropTypes.string,
  title: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  items: PropTypes.array.isRequired,
  expanded: PropTypes.bool.isRequired,
  toggleExpanded: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
};

const formatDate = dateString => {
  return formatCustomDate(dateString);
};

export default ActionMenu;
